// API error normalization.
// Converts ky HTTPError and network errors into a consistent shape
// for display in notifications and inline form errors.

import { HTTPError } from 'ky';

export interface ApiError {
  status: number;
  message: string;
  fieldErrors?: Record<string, string[]>;
  /** Business error code — populated from the response body `error` field (e.g. BusinessException.Code). */
  code?: string;
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  /** FluentValidation failures arrive as a JSON-encoded { field: [msg] } string (task 333.6). */
  details?: string;
  /**
   * Either FluentValidation field errors ({ field: [msg] }) or — from the GL manual-entry and
   * other Result-based endpoints — a plain list of messages (`{ errors: ["…"] }`, task 333.6).
   */
  errors?: Record<string, string[]> | string[];
}

/** Field-error dictionary when the body carries one; undefined for the string-list shape. */
function fieldErrorsOf(body: ApiErrorBody): Record<string, string[]> | undefined {
  if (body.errors && !Array.isArray(body.errors)) return body.errors;
  if (typeof body.details === 'string') {
    try {
      const parsed = JSON.parse(body.details) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, string[]>;
      }
    } catch {
      /* details is free text — fall through */
    }
  }
  return undefined;
}

/**
 * Best human-readable message for a response body. Order: explicit `message`, then a string-list
 * `errors` joined, then the field errors flattened, then the HTTP status. `statusText` is empty
 * under HTTP/2, so it is never relied on (that left modals showing nothing — task 333.6).
 */
function messageOf(body: ApiErrorBody, status: number, statusText: string): string {
  const fields = fieldErrorsOf(body);
  if (fields) {
    // Field failures are the actionable part; the generic "One or more validation errors
    // occurred" message hides them.
    const flat = Object.values(fields).flat().filter(Boolean);
    if (flat.length > 0) return flat.join(' ');
  }
  if (body.message) return body.message;
  if (Array.isArray(body.errors) && body.errors.length > 0) return body.errors.join(' ');
  if (body.error) return body.error;
  return statusText ? `${statusText} (${status})` : `Request failed with status code ${status}`;
}

async function parseErrorBody(response: Response): Promise<ApiErrorBody> {
  try {
    return (await response.clone().json()) as ApiErrorBody;
  } catch {
    return {};
  }
}

export async function normalizeError(error: unknown): Promise<ApiError> {
  // ky wraps HTTP errors in HTTPError with a .response property.
  if (error instanceof HTTPError) {
    const body = await parseErrorBody(error.response);
    return {
      status: error.response.status,
      message: messageOf(body, error.response.status, error.response.statusText),
      fieldErrors: fieldErrorsOf(body),
      code: body.error,
    };
  }

  // Raw fetch Response (fallback for non-ky callers).
  if (error instanceof Response) {
    const body = await parseErrorBody(error);
    return {
      status: error.status,
      message: messageOf(body, error.status, error.statusText),
      fieldErrors: fieldErrorsOf(body),
      code: body.error,
    };
  }

  if (error instanceof Error) {
    return { status: 0, message: error.message };
  }

  return { status: 0, message: 'An unexpected error occurred' };
}

/** Shown when a document call returns 404: the document is hidden from the caller (task 401.4 view rules). */
export const DOCUMENT_NOT_AVAILABLE_MESSAGE =
  'This document is not available to your role (or no longer exists).';

/**
 * Message for a failed call on a document (transition, override, provider re-query — task 401.12).
 * A 404 means the document is hidden from the caller's role; anything else is shown as the api returned it
 * (the engine's 400 `errors[]` already names the missing permission / the previous actor).
 */
export async function documentActionErrorMessage(error: unknown): Promise<string> {
  const apiError = await normalizeError(error);
  return apiError.status === 404 ? DOCUMENT_NOT_AVAILABLE_MESSAGE : apiError.message;
}

/**
 * HTTP status of a failed request (ky HTTPError or a raw Response), synchronously — for render-time decisions
 * such as "404 → not available" where normalizeError's async body parse is not needed. Null for anything else.
 */
export function httpStatusOf(error: unknown): number | null {
  if (error instanceof HTTPError) return error.response.status;
  if (error instanceof Response) return error.status;
  return null;
}
